import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Edit, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { z } from 'zod';

import { ClientFormModal } from '@apps/web/components/admin/client_form_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useAdminClient } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const clientDetailSearchSchema = z.object({
    organizationId: z.string().optional(),
    search: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/clients/$client-id')({
    head: () => ({
        meta: [
            {
                title: 'Client Details | Phoebe Admin',
            },
        ],
    }),
    validateSearch: clientDetailSearchSchema,
    component: ClientDetailPage,
});

function ClientDetailPage() {
    const { 'client-id': clientId } = Route.useParams();
    const returnSearch = Route.useSearch();

    return (
        <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="max-w-5xl p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/clients"
                        search={returnSearch}
                        replace={true}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Clients
                    </Link>
                </div>
                <ClientDetails clientId={clientId} />
            </div>
        </div>
    );
}

function ClientDetails({ clientId }: { clientId: string }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const router = useRouter();
    const { mutate } = useSWRConfig();

    const {
        data: client,
        isLoading,
        mutate: mutateClient,
    } = useAdminClient(clientId);

    const handleDelete = async () => {
        if (!clientId || !client) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete ${client.first_name} ${client.last_name}? This action cannot be undone.`,
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await getApiClient().delete({
                url: '/admin/clients/{client_id}',
                pathParams: { client_id: clientId },
            });

            toast.success('Client deleted successfully', {
                persistNotification: false,
            });
            mutate(
                (key) =>
                    typeof key === 'string' && key.includes('/admin/clients'),
            );
            router.navigate({ to: '/admin/clients' });
        } catch (error) {
            toast.error('Failed to delete client', {
                persistNotification: false,
            });
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading client details…</span>
                </div>
            </div>
        );
    }

    if (!client) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Client not found
            </div>
        );
    }

    const formatAddress = () => {
        const parts = [
            client.address_line_1,
            client.address_line_2,
            [client.city, client.state, client.postal_code]
                .filter(Boolean)
                .join(', '),
        ].filter(Boolean);
        return parts.length > 0 ? parts.join('\n') : null;
    };

    return (
        <div className="max-w-full min-w-0 space-y-6 overflow-hidden">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Client Details</h1>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                        className="gap-2"
                    >
                        <Edit className="h-4 w-4" />
                        Edit
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="gap-2 text-red-600 hover:text-red-700"
                    >
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Badge variant={client.is_active ? 'default' : 'secondary'}>
                        {client.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>
            <Card className="p-6">
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Name
                            </h3>
                            <p className="mt-1">
                                {client.first_name} {client.last_name}
                            </p>
                        </div>
                        {client.preferred_name && (
                            <div>
                                <h3 className="text-muted-foreground text-sm font-medium">
                                    Preferred Name
                                </h3>
                                <p className="mt-1">{client.preferred_name}</p>
                            </div>
                        )}
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Organization
                            </h3>
                            <p className="mt-1">{client.organization_name}</p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Mode
                            </h3>
                            <Badge variant="outline" className="mt-1">
                                {client.mode === 'sandbox' ? 'Sandbox' : 'Live'}
                            </Badge>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Email
                            </h3>
                            <p className="mt-1">
                                {client.email || 'Not provided'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Phone Number
                            </h3>
                            <p className="mt-1">
                                {client.phone_number || 'Not provided'}
                            </p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Address
                            </h3>
                            <p className="mt-1 whitespace-pre-line">
                                {formatAddress() || 'Not provided'}
                            </p>
                        </div>
                        {client.office_name && (
                            <div>
                                <h3 className="text-muted-foreground text-sm font-medium">
                                    Office
                                </h3>
                                <p className="mt-1">{client.office_name}</p>
                            </div>
                        )}
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Created
                            </h3>
                            <p className="mt-1">
                                {new Date(client.created_at).toLocaleString()}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Last Updated
                            </h3>
                            <p className="mt-1">
                                {new Date(client.updated_at).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
                {client.care_plan && (
                    <div className="mt-6 border-t pt-4">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Care Plan
                        </h3>
                        <p className="mt-2 text-sm whitespace-pre-wrap">
                            {client.care_plan}
                        </p>
                    </div>
                )}
            </Card>
            <ClientFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                mode="edit"
                clientId={clientId}
                onSaveSuccess={() => mutateClient()}
                initialData={{
                    mode: client.mode,
                    organization_id: client.organization_id,
                    first_name: client.first_name,
                    last_name: client.last_name,
                    preferred_name: client.preferred_name || '',
                    email: client.email || '',
                    phone_number: client.phone_number || '',
                    address_line_1: client.address_line_1 || '',
                    address_line_2: client.address_line_2 || '',
                    city: client.city || '',
                    state: client.state || '',
                    postal_code: client.postal_code || '',
                    country: client.country || 'US',
                    office_name: client.office_name || '',
                    care_plan: client.care_plan || '',
                    is_active: client.is_active,
                }}
            />
        </div>
    );
}
